import { SetupPlatformId } from './platforms';

export type InstructionItem = {
  text?: string;
  label?: string;
  value?: string;
  valueRef?: string;
  secret?: boolean;
};
export type InstructionBlock = {
  type: string;
  content?: string;
  lead?: string;
  label?: string;
  artifact?: string;
  valueRef?: string;
  fallback?: string;
  summary?: string;
  items: InstructionItem[];
  blocks: InstructionBlock[];
};
export type InstallSource = {
  label: string;
  url: string;
  version?: string;
  note?: string;
};
export type InstructionClient = {
  id: string;
  app: string;
  recommended: boolean;
  badge?: string;
  configKind?: string;
  sources: InstallSource[];
  blocks: InstructionBlock[];
  troubleshooting: InstructionBlock[];
};
export type SetupInstructions = {
  schemaVersion: 1;
  locale: 'ru' | 'en';
  platforms: {
    platformId: SetupPlatformId;
    label: string;
    clients: InstructionClient[];
  }[];
};
export const VALUE_REFS = [
  'SERVER_DOMAIN',
  'SERVER_PORT',
  'USER_NAME',
  'PASSWORD',
  'SUB_URL',
  'HIDDIFY_DEEPLINK',
] as const;
export type SetupValues = Partial<Record<(typeof VALUE_REFS)[number], string>>;
const record = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const text = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v : undefined;
export const safeHttps = (v: unknown): v is string => {
  if (
    typeof v !== 'string' ||
    Array.from(v).some((char) => char.charCodeAt(0) <= 32)
  )
    return false;
  try {
    const url = new URL(v);
    return (
      url.protocol === 'https:' &&
      !!url.hostname &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
};
export const safeDeeplink = (v: unknown): v is string => {
  if (
    typeof v !== 'string' ||
    Array.from(v).some((char) => char.charCodeAt(0) <= 32)
  )
    return false;
  try {
    const url = new URL(v);
    return (
      url.protocol === 'hiddify:' &&
      url.hostname === 'install-config' &&
      (url.pathname === '' || url.pathname === '/') &&
      !url.username &&
      !url.password &&
      !url.hash &&
      !url.port &&
      Array.from(url.searchParams.keys()).every((key) => key === 'url') &&
      url.searchParams.getAll('url').length === 1 &&
      safeHttps(url.searchParams.get('url'))
    );
  } catch {
    return false;
  }
};
export const resolveValue = (
  item: InstructionItem,
  values: SetupValues
): string | undefined => {
  if (item.valueRef) {
    return VALUE_REFS.includes(item.valueRef as (typeof VALUE_REFS)[number])
      ? values[item.valueRef as (typeof VALUE_REFS)[number]]
      : undefined;
  }
  return item.value;
};
export const parseValues = (value: unknown): SetupValues => {
  const data = record(value);
  return Object.fromEntries(
    VALUE_REFS.flatMap((key) =>
      typeof data[key] === 'string' ? [[key, data[key]]] : []
    )
  );
};
const blocks = (value: unknown, depth = 0): InstructionBlock[] =>
  depth > 8
    ? []
    : list(value).flatMap((raw) => {
        const b = record(raw);
        if (
          ![
            'text',
            'muted',
            'subheading',
            'notice',
            'warning',
            'sources',
            'download',
            'steps',
            'params',
            'deeplink',
            'details',
          ].includes(String(b.type))
        )
          return [];
        return [
          {
            type: String(b.type),
            content: text(b.content),
            lead: text(b.lead),
            label: text(b.label),
            artifact: text(b.artifact),
            valueRef: text(b.valueRef),
            fallback: text(b.fallback),
            summary: text(b.summary),
            blocks: blocks(b.blocks, depth + 1),
            items: list(b.items).flatMap((rawItem) => {
              const i = record(rawItem);
              if (!text(i.text) && !text(i.label) && !text(i.name)) return [];
              return [
                {
                  text: text(i.text),
                  label: text(i.label) ?? text(i.name),
                  value: text(i.value),
                  valueRef: text(i.valueRef),
                  secret: i.secret === true || i.valueRef === 'PASSWORD',
                },
              ];
            }),
          },
        ];
      });
const clientFor = (
  raw: unknown,
  platform: string
): InstructionClient | undefined => {
  const c = record(raw);
  if (!text(c.id) || !text(c.app) || !list(c.platforms).includes(platform))
    return undefined;
  const sources = list(c.sources)
    .map(record)
    .filter(
      (s) =>
        s.platform === platform &&
        s.inList !== false &&
        ['recommended', 'available'].includes(String(s.status))
    )
    .sort(
      (a, b) =>
        Number(b.status === 'recommended') - Number(a.status === 'recommended')
    )
    .flatMap((s) =>
      safeHttps(s.url) && text(s.label)
        ? [
            {
              label: String(s.label),
              url: s.url,
              version: text(s.version),
              note: text(s.note),
            },
          ]
        : []
    );
  if (!sources.length || !Array.isArray(c.blocks)) return undefined;
  return {
    id: String(c.id),
    app: String(c.app),
    recommended: c.recommended === true,
    badge: text(c.badge),
    configKind: text(c.configKind),
    sources,
    blocks: blocks(c.blocks),
    troubleshooting: blocks(c.troubleshooting),
  };
};
export const parseInstructions = (
  value: unknown,
  locale: 'ru' | 'en'
): SetupInstructions | undefined => {
  const data = record(value);
  if (data.schemaVersion !== 1 || data.locale !== locale) return undefined;
  const platforms = list(data.platforms).flatMap((raw) => {
    const p = record(raw);
    const id = p.platformId ?? p.id;
    if (
      !['android', 'ios', 'windows', 'macos'].includes(String(id)) ||
      !text(p.label)
    )
      return [];
    let clients = list(data.clients)
      .slice()
      .sort(
        (a, b) =>
          (Number(record(a).order) || 0) - (Number(record(b).order) || 0)
      )
      .flatMap((c) => {
        const parsed = clientFor(c, String(id));
        return parsed ? [parsed] : [];
      });
    if (
      !clients.length &&
      safeHttps(p.installUrl) &&
      text(p.appName) &&
      text(p.installLabel) &&
      text(p.configKind) &&
      list(p.steps).length &&
      list(p.steps).every((step) => !!text(step))
    ) {
      clients = [
        {
          id: String(p.configKind),
          app: String(p.appName),
          recommended: true,
          badge: text(p.badge),
          configKind: String(p.configKind),
          sources: [
            {
              label: String(p.installLabel),
              url: p.installUrl,
              version: text(p.version),
            },
          ],
          blocks: blocks([
            { type: 'sources' },
            { type: 'download', artifact: p.configKind },
            {
              type: 'steps',
              items: list(p.steps).map((step) => ({ text: step })),
            },
            ...(p.notice ? [{ type: 'notice', content: p.notice }] : []),
          ]),
          troubleshooting: [],
        },
      ];
    }
    clients = clients.filter(
      (c, index) => clients.findIndex((other) => other.id === c.id) === index
    );
    return clients.length
      ? [{ platformId: id as SetupPlatformId, label: String(p.label), clients }]
      : [];
  });
  if (
    !list(data.clients).length &&
    platforms.length !== list(data.platforms).length
  )
    return undefined;
  return platforms.length ? { schemaVersion: 1, locale, platforms } : undefined;
};

export const needsValues = (blocks: InstructionBlock[]): boolean =>
  blocks.some(
    (block) =>
      (block.valueRef !== undefined &&
        VALUE_REFS.includes(block.valueRef as (typeof VALUE_REFS)[number])) ||
      block.items.some(
        (item) =>
          item.valueRef !== undefined &&
          VALUE_REFS.includes(item.valueRef as (typeof VALUE_REFS)[number])
      ) ||
      needsValues(block.blocks)
  );
